// ============================================================
// src/api/common/axios.ts - Axios singleton with interceptors
// ============================================================
import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import { ENV } from '@/config/env';
import { APP_CONSTANTS } from '@/constants';
import { StorageUtil } from '@/utils/storage/storageUtil';

// ── Create instance ───────────────────────────────────────────────────────
const apiClient: AxiosInstance = axios.create({
  baseURL: ENV.API_BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ── Request interceptor - inject JWT ──────────────────────────────────────
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = StorageUtil.get<string>(APP_CONSTANTS.TOKEN_KEY);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ── Flag to prevent multiple refresh calls ────────────────────────────────
let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: AxiosError) => void;
}[] = [];

const processQueue = (error: AxiosError | null, token: string | null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else if (token) prom.resolve(token);
  });
  failedQueue = [];
};

// ── Response interceptor - handle 401 + refresh token ─────────────────────
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ error?: string; message?: string }>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const status = error.response?.status;

    // 401 → attempt token refresh
    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = StorageUtil.get<string>(
          APP_CONSTANTS.REFRESH_TOKEN_KEY,
        );
        if (!refreshToken) throw new Error('No refresh token');

        const { data } = await axios.post(
          `${ENV.API_BASE_URL}/auth/refresh`,
          JSON.stringify(refreshToken),
          { headers: { 'Content-Type': 'application/json' } },
        );

        StorageUtil.set(APP_CONSTANTS.TOKEN_KEY, data.token);
        StorageUtil.set(APP_CONSTANTS.REFRESH_TOKEN_KEY, data.refreshToken);

        processQueue(null, data.token);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${data.token}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null);
        // Force logout
        StorageUtil.remove(APP_CONSTANTS.TOKEN_KEY);
        StorageUtil.remove(APP_CONSTANTS.REFRESH_TOKEN_KEY);
        StorageUtil.remove(APP_CONSTANTS.USER_KEY);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // 403 → redirect to unauthorized (skip if already there to avoid reload loops)
    if (status === 403 && window.location.pathname !== '/unauthorized') {
      window.location.href = '/unauthorized';
    }

    return Promise.reject(error);
  },
);

export default apiClient;
